import './container-page.scss'

import * as _ from 'lodash'
import { forkJoin, of } from 'rxjs'
import {
    defaultIfEmpty,
    delay,
    map,
    pluck,
    retry,
    retryWhen,
    switchMap,
    toArray,
    tap,
    concatMap,
} from 'rxjs/operators'
import Component from 'vue-class-component'
import {
    faExclamationTriangle,
    faCheck,
    faTimes,
    faCircleNotch,
} from '@fortawesome/free-solid-svg-icons'

import { API_ENDPOINTS, Container, ContainerDataStore, Item, ItemDataStore } from '@skinsoft/entity'
import { ContainerFeaturedCard, containerSelectors } from '@skinsoft/entity-ui/src/container'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { AddAccessoryModal, AddContainerModal, ItemCard, Touchable } from '@skinsoft/ui'
import {
    ADD_ACCESSORY_TOPIC,
    ADD_CONTAINER_TOPIC,
    ADD_ITEM_TOPIC,
    ADD_LOCATION_TOPIC,
    CLOSE_CONTAINER_TOPIC,
    DELETE_CONTAINER_TOPIC,
    OPEN_CONTAINER_TOPIC,
} from '../../../busTopics'
import { ContainerCard } from '@skinsoft/entity-ui'
import { SelectItemMultipleModal } from '../../component/select-item-multiple-modal'
import { ContainerItemList } from './container-item-list'
import { getContentTypeFromTypeKey, ListContentType } from './list-content-type'
import { concat, identity } from 'lodash'
import { ChooseItemModal } from '../../component/choose-item-modal'
import { Routes } from '../../../router'
import { DELETE_CARD } from '@skinsoft/events'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'

declare var ITEM_SEARCH_TYPES: string[]

@Component({
    components: {
        sItemCard: ItemCard,
        sContainerFeaturedCard: ContainerFeaturedCard,
        sSelectItemMultipleModal: SelectItemMultipleModal,
        sTouchable: Touchable,
        sAddAccessoryModal: AddAccessoryModal,
        sAddContainerModal: AddContainerModal,
        sContainerCard: ContainerCard,
        sContainerItemList: ContainerItemList,
        sChooseItemModal: ChooseItemModal
    },
    template: require('./container-page.html'),
})
export class ContainerPage extends ReactiveVue {
    public $containerDataStore: ContainerDataStore
    public $itemDataStore: ItemDataStore
    public exclamationTriangleIcon = faExclamationTriangle
    public checkIcon = faCheck
    public crossIcon = faTimes
    public spinnerIcon = faCircleNotch
    public $refs: {
        addItemModal: SelectItemMultipleModal
        addAccessoryModal: SelectItemMultipleModal
        addContainerModal: SelectItemMultipleModal
        chooseItemModal: ChooseItemModal
    }

    ListContentType = ListContentType
    public itemTypes = ITEM_SEARCH_TYPES
    public containerTypes = ['physicalContainerItem', 'numericContainerItem']
    public accessoryTypes = ['physicalAccessoryItem', 'numericAccessoryItem']

    private container: Container = null as any
    private items = [null, null, null] as any
    private subContainers = [] as any
    private accessories = [] as any
    private currentlyAddingTo: ListContentType | null = null

    private DELETE_CARD = DELETE_CARD

    public mounted() {
        this.subscribeToBus(ADD_ITEM_TOPIC, this.openAddItemModal)
        this.subscribeToBus(ADD_ACCESSORY_TOPIC, () => this.openAddAccessoryModal())
        this.subscribeToBus(CLOSE_CONTAINER_TOPIC, () => this.toggleContainerOpen(false))
        this.subscribeToBus(OPEN_CONTAINER_TOPIC, () => this.toggleContainerOpen())
        this.subscribeToBus(ADD_CONTAINER_TOPIC, this.openAddContainerModal)
        this.subscribeToBus(ADD_LOCATION_TOPIC, this.openLocationModal)
        this.subscribeToBus(DELETE_CONTAINER_TOPIC, this.deleteContainer)
        this.subscribeToEntity()
    }

    public subscribeToEntity() {
        this.subscribeTo(
            this.$containerDataStore.fetchOne(this.$route.params.id),
            (container) => {
                this.container = container
                this.subscribeToItems()
            },
            'container',
        )
    }

    public async subscribeToItems() {
        this.subscribeTo(
            of(this.container.id).pipe(
                switchMap((id) =>
                    this.$containerDataStore.getAssociatedItems(
                        id,
                        ITEM_SEARCH_TYPES.concat(this.containerTypes).concat(this.accessoryTypes),
                    ),
                ),
                switchMap((list) => concat(list)),
                concatMap(identity),
                map(item => ({...item, isBeingDeleted: false})),
                toArray(),
                defaultIfEmpty([] as Item[]),
                tap((item) => {
                    if (
                        !(this.items.length > 0 && !this.items[0]) &&
                        item.length ===
                            this.items.length + this.subContainers.length + this.accessories.length
                    ) {
                        throw new Error('No changes and not in first load')
                    }
                }),
                retryWhen((e) => e.pipe(delay(1000))),
                tap(
                    (items) =>
                        (this.subContainers = items.filter((item) =>
                            item.type.includes('Container'),
                        )),
                ),
                map((items) => items.filter((item) => !item.type.includes('Container'))),
                tap(
                    (items) =>
                        (this.accessories = items.filter((item) =>
                            item.type.includes('Accessory'),
                        )),
                ),
                map((items) => items.filter((item) => !item.type.includes('Accessory'))),
                tap(() => this.currentlyAddingTo = null)
            ),
            (items) => (this.items = items),
            'items',
        )
    }

    private async onAddedItemSelected({ id, type }: { id: string, type: string}) {
        this.currentlyAddingTo = getContentTypeFromTypeKey(type)
        this.$containerDataStore
            .addItem(this.container, id)
            .pipe(delay(1000))
            .subscribe(() => {
                this.unsubscribeFrom('items')
                this.unsubscribeFrom('container')
                this.subscribeToEntity()
            })
    }

    private openAddItemModal() {
        this.$refs.addItemModal.open()
    }

    private openAddAccessoryModal() {
        this.$refs.addAccessoryModal.open()
    }

    private openAddContainerModal() {
        this.$refs.addContainerModal.open()
    }

    private toggleContainerOpen(open: boolean = true) {
        if (
                (
                    this.container.containerItemSchema.openContainer 
                    && (this.container.locationItemSchema?.permanentLocation || this.container.locationItemSchema?.realTimeLocation)
                ) 
                || !this.container.containerItemSchema.openContainer
            ) {
            this.container.containerItemSchema.openContainer = open
            this.$containerDataStore.update(
                this.container.id,
                this.container,
                API_ENDPOINTS[this.container.type],
            ).subscribe(() => {
                const alertPayload = {
                    message: open ? this.$t('container.alerts.containerOpened') : this.$t('container.alerts.containerClosed'),
                    variant: "success"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            })
        }
    }

    private onLongPress(itemIndex: number, listType: ListContentType) {
        this.onCancel()
        if (listType === ListContentType.CONTAINER) {
            this.subContainers[itemIndex].isBeingDeleted = true
        } else if (listType === ListContentType.ITEM) {
            this.items[itemIndex].isBeingDeleted = true
        } else {
            this.accessories[itemIndex].isBeingDeleted = true
        }
    }

    private onCancel() {
        this.items.forEach((item) => (item.isBeingDeleted = false))
        this.subContainers.forEach((container) => (container.isBeingDeleted = false))
        this.accessories.forEach((accessory) => (accessory.isBeingDeleted = false))
    }

    private onDelete(item: Item) {
        this.$containerDataStore
            .removeItem(this.container?.id, item)
            .pipe(delay(1000))
            .subscribe(() => {
                this.unsubscribeFrom('container')
                this.unsubscribeFrom('items')
                this.subscribeToEntity()
            })
    }

    private deleteContainer() {
        if(this.container.id) {
            this.$containerDataStore.delete(this.container.id).subscribe(() => {
                this.$router.push({ name: Routes.CONTAINERS })
            })
        }
    }

    private get itemIds() {
        return this.items.filter(identity).map((item) => item.id)
    }

    private get containerIds() {
        return this.subContainers.filter(identity).map((container) => container.id)
    }

    private get accessoryIds() {
        return this.accessories.filter(identity).map((accessory) => accessory.id)
    }

    private openLocationModal() {
        this.$refs.chooseItemModal.open()
    }

    private addLocation({ location: { id: locationId }, setPermanentLocation }: { location: { id: string }, setPermanentLocation: boolean }) {
        let locationPath: string
        if (setPermanentLocation) {
            locationPath = 'locationItemSchema.permanentLocation.permanentLocation'
        } else {
            locationPath = 'locationItemSchema.realTimeLocation.realTimeLocation'
        }
        _.set(this.container, locationPath, locationId)
        this.$containerDataStore.update(this.container.id, this.container).subscribe(() => {
            this.unsubscribeFrom('container')
            this.unsubscribeFrom('items')
            this.subscribeToEntity()
        })
    }
}
